import React, { useState } from "react";
import {
  Search,
  Plus,
  LayoutGrid,
  Package,
  Eye,
  EyeOff,
  Layers,
  ChevronDown,
  SlidersHorizontal,
  PackageCheck,
  Utensils,
} from "lucide-react";

const CATALOGUE_TABS = [
  {
    key: "catalogue",
    label: "Catalogue",
    icon: LayoutGrid,
  },
  {
    key: "outofstock",
    label: "Out of Stock",
    icon: Package,
  },
  {
    key: "invisibleStock",
    label: "Invisible Stock",
    icon: Eye,
  },
  {
    key: "rawInventory",
    label: "Raw Inventory",
    icon: Layers,
  },
];

export default function CatalogueView({
  catalogueTab,
  setCatalogueTab,

  searchText,
  setSearchText,

  userID,
  handleOpenCatalogueImage,
  handleUpdateSizeId,
  setMasterCategoryOpen,
  setAddPrinterOpen,

  loading,
  filteredCategories,
  expandedCategories,
  toggleCategory,

  rawItemName,
  setRawItemName,
  addingRawItem,
  handleAddRawInventory,
  loadingRaw,
  rawInventoryList,

  isItemPackagingEnabled,
  packagingBusyId,
  togglePackagingCharge,

  markAnchor,
  openCustomization,
  openViewItem,
  openAddVariant,

  variantStatusMap,
  handleVariantStatusToggle,

  itemVisibilityMap,
  handleHideItem,
}) {
  const [variantMoreModal, setVariantMoreModal] = useState(null);

  return (
    <>
      {/* Filters / Actions bar */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-white p-2">
        <div className="flex items-center gap-6 border-b pb-2">
          {CATALOGUE_TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setCatalogueTab(key)}
              className={`pb-2 flex items-center gap-2 border-b-2 transition ${
                catalogueTab === key
                  ? "border-green-600 text-green-600"
                  : "border-transparent text-gray-500 hover:text-green-600"
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-gray-100 text-gray-700">
            <Search size={16} />
          </div>

          <input
            className="h-9 w-[260px] rounded-lg border px-3 text-sm outline-none"
            placeholder="Search items..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center gap-2 rounded-lg border bg-[#01c37d] px-3 text-sm text-white hover:border-[#01c37d] hover:bg-white hover:text-[#323333] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleOpenCatalogueImage}
            disabled={!userID}
            title={userID ? "Manage catalogue images" : "Select a client first"}
          >
            <Plus size={16} />
            Catalogue Image
          </button>

          <button
            className="inline-flex h-9 items-center gap-2 rounded-lg border bg-[#01c37d] px-3 text-sm text-white hover:border-[#01c37d] hover:bg-white hover:text-[#323333]"
            onClick={() => handleUpdateSizeId(userID)}
            disabled={!userID}
          >
            <Plus size={16} />
            Update Size ID
          </button>

          <button
            className="inline-flex h-9 items-center gap-2 rounded-lg border bg-white px-3 text-sm hover:bg-gray-50"
            onClick={() => setMasterCategoryOpen(true)}
          >
            <Plus size={16} />
            Add Master Category
          </button>
          <button
            className="inline-flex h-9 items-center gap-2 rounded-lg border bg-gray-100 px-3 text-sm text-gray-400 cursor-not-allowed"
            disabled
            title="Add Printer temporarily disabled"
          >
            <Plus size={16} />
            Add Printer
          </button>
        </div>
      </div>

      <div className="rounded-xl border bg-white">
        {catalogueTab === "rawInventory" ? (
          <RawInventorySection
            rawItemName={rawItemName}
            setRawItemName={setRawItemName}
            addingRawItem={addingRawItem}
            handleAddRawInventory={handleAddRawInventory}
            loadingRaw={loadingRaw}
            rawInventoryList={rawInventoryList}
          />
        ) : (
          <CatalogueItemsSection
            loading={loading}
            filteredCategories={filteredCategories}
            expandedCategories={expandedCategories}
            toggleCategory={toggleCategory}
            isItemPackagingEnabled={isItemPackagingEnabled}
            packagingBusyId={packagingBusyId}
            togglePackagingCharge={togglePackagingCharge}
            markAnchor={markAnchor}
            openCustomization={openCustomization}
            openViewItem={openViewItem}
            openAddVariant={openAddVariant}
            variantStatusMap={variantStatusMap}
            handleVariantStatusToggle={handleVariantStatusToggle}
            itemVisibilityMap={itemVisibilityMap}
            handleHideItem={handleHideItem}
            setVariantMoreModal={setVariantMoreModal}
          />
        )}
      </div>

      {variantMoreModal && (
        <VariantMoreModal
          itemName={variantMoreModal.itemName}
          prices={variantMoreModal.prices}
          variantStatusMap={variantStatusMap}
          handleVariantStatusToggle={handleVariantStatusToggle}
          onClose={() => setVariantMoreModal(null)}
        />
      )}
    </>
  );
}

function RawInventorySection({
  rawItemName,
  setRawItemName,
  addingRawItem,
  handleAddRawInventory,
  loadingRaw,
  rawInventoryList,
}) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Raw Inventory</h2>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Enter raw item name"
            value={rawItemName}
            onChange={(e) => setRawItemName(e.target.value)}
            className="h-11 w-[260px] rounded-md border-2 border-gray-300 px-3 text-sm outline-none focus:border-green-500"
          />

          <button
            onClick={handleAddRawInventory}
            disabled={addingRawItem}
            className="h-11 rounded-md bg-green-600 px-5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {addingRawItem ? "Adding..." : "Add Raw Item"}
          </button>
        </div>
      </div>

      {loadingRaw ? (
        <div className="py-10 text-center text-gray-500">
          Loading Raw Inventory...
        </div>
      ) : rawInventoryList.length === 0 ? (
        <div className="py-10 text-center text-gray-400">
          No raw inventory found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full overflow-hidden rounded-lg border text-sm">
            <thead className="bg-green-100 text-gray-700">
              <tr>
                <th className="border-b px-4 py-3 text-left w-[80px]">#</th>
                <th className="border-b px-4 py-3 text-left w-[120px]">ID</th>
                <th className="border-b px-4 py-3 text-left">Item Name</th>
              </tr>
            </thead>

            <tbody>
              {rawInventoryList.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="border-b px-4 py-3">{index + 1}</td>
                  <td className="border-b px-4 py-3">{item.id || index + 1}</td>
                  <td className="border-b px-4 py-3">{item.item_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CatalogueItemsSection({
  loading,
  filteredCategories,
  expandedCategories,
  toggleCategory,
  isItemPackagingEnabled,
  packagingBusyId,
  togglePackagingCharge,
  markAnchor,
  openCustomization,
  openViewItem,
  openAddVariant,
  variantStatusMap,
  handleVariantStatusToggle,
  itemVisibilityMap,
  handleHideItem,
  setVariantMoreModal,
}) {
  if (loading) {
    return (
      <div className="py-16 text-center text-gray-500">Loading catalogue…</div>
    );
  }

  if (filteredCategories.length === 0) {
    return (
      <div className="py-16 text-center text-gray-400">No items found.</div>
    );
  }

  return (
    <>
      {filteredCategories.map((category) => {
        const catId = String(category.cat_id);
        const isOpen = expandedCategories.includes(catId);
        const items = isOpen ? category.menu_items : [];

        return (
          <div key={catId} className="border-b last:border-none">
            {/* Category Row */}
            <div className="flex items-center justify-between px-3 py-3">
              <div className="flex items-center gap-2">
                <button
                  className="grid h-8 w-8 place-items-center rounded-md border"
                  onClick={() => toggleCategory(catId)}
                  title={isOpen ? "Collapse" : "Expand"}
                >
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div className="text-base font-semibold text-gray-800">
                  {category.category_name}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="inline-flex h-9 items-center gap-2 rounded-lg border bg-white px-3 text-sm hover:bg-gray-50"
                  onClick={() => openCustomization(category)}
                >
                  <SlidersHorizontal size={16} />
                  Customize
                </button>

                <button
                  className="inline-flex h-9 items-center gap-2 rounded-lg border bg-white px-3 text-sm hover:bg-gray-50"
                  onClick={() => toggleCategory(catId)}
                >
                  {isOpen ? "Hide Items" : "Show Items"}
                </button>
              </div>
            </div>

            {isOpen && (
              <div className="sticky top-0 z-[1] grid grid-cols-12 gap-2 border-y bg-[#01C37D] px-3 py-2 text-xs font-semibold text-white">
                <div className="col-span-3">Item</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-7">Actions</div>
              </div>
            )}

            {isOpen &&
              items.map((item, index) => (
                <div
                  key={`${item.item_id}-${index}`}
                  className="grid min-h-[64px] grid-cols-12 items-center gap-2 border-b px-2 py-1 text-xs"
                >
                  <div className="col-span-3 flex min-w-0 items-center gap-2">
                    <ItemImage src={item.photo} alt={item.item_name} />

                    <div className="min-w-0">
                      <div className="truncate text-xs font-semibold text-gray-900">
                        {item.item_name}
                      </div>
                      <div className="truncate text-[11px] text-gray-500">
                        {(item?.prices?.[0]?.size || "").toString()}
                      </div>
                    </div>
                  </div>

                  <div className="col-span-2 whitespace-nowrap text-xs font-bold">
                    {item?.prices?.[0]?.formatted_price +
                      " | T.A : ₹" +
                      item?.prices?.[0]?.takeaway_price +
                      " | H.D : ₹" +
                      item?.prices?.[0]?.home_delivery_price}
                  </div>

                  <div className="col-span-7 flex min-w-0 items-center gap-2 px-1 py-0">
                    {isItemPackagingEnabled && (
                      <PackagingButton
                        item={item}
                        category={category}
                        packagingBusyId={packagingBusyId}
                        togglePackagingCharge={togglePackagingCharge}
                        markAnchor={markAnchor}
                      />
                    )}

                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        className="flex items-center justify-center rounded-lg gap-2 bg-[#01C37D] text-white hover:bg-green-700 p-2"
                        title="View Item"
                        style={{
                          backgroundColor: "#16a34a",
                          color: "#fff",
                          width: "6.3rem",
                          height: "30px",
                          fontSize: "12px",
                          padding: "4px 8px",
                        }}
                        onClick={() => openViewItem(item, category)}
                      >
                        <Eye size={16} />
                        View Item
                      </button>

                      <button
                        className="flex items-center justify-center rounded-lg gap-2 p-2 bg-[#01C37D] text-white hover:bg-[#00a86b]"
                        title="Add Variant"
                        style={{
                          backgroundColor: "#01C37D",
                          color: "#fff",
                          width: "6.5rem",
                          height: "30px",
                          fontSize: "12px",
                          padding: "4px 8px",
                        }}
                        onClick={() => openAddVariant(item)}
                      >
                        <Plus size={16} />
                        Add Variant
                      </button>
                    </div>

                    <VariantStatusList
                      item={item}
                      prices={item.prices || []}
                      variantStatusMap={variantStatusMap}
                      handleVariantStatusToggle={handleVariantStatusToggle}
                      setVariantMoreModal={setVariantMoreModal}
                    />

                    <ItemVisibilityButton
                      item={item}
                      itemVisibilityMap={itemVisibilityMap}
                      handleHideItem={handleHideItem}
                    />
                  </div>
                </div>
              ))}
          </div>
        );
      })}
    </>
  );
}

function ItemImage({ src, alt }) {
  const [imgError, setImgError] = useState(false);

  const hasImage = src && String(src).trim() !== "" && !imgError;

  if (!hasImage) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-green-100 bg-green-50 text-green-600">
        <Utensils size={20} strokeWidth={2.2} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt || "Food item"}
      className="h-10 w-10 shrink-0 rounded border object-cover"
      loading="lazy"
      onError={() => setImgError(true)}
    />
  );
}

function PackagingButton({
  item,
  category,
  packagingBusyId,
  togglePackagingCharge,
  markAnchor,
}) {
  const pkg = String(item.packaging_charge || "N").toUpperCase();
  const isOn = pkg === "Y";
  const busy = packagingBusyId === String(item.item_id);

  return (
    <button
      title={isOn ? "Package Charges: ON" : "Package Charges: OFF"}
      disabled={busy}
      onClick={() => {
        markAnchor(category.cat_id, item.item_id);
        togglePackagingCharge(item);
      }}
      style={{
        backgroundColor: isOn ? "#16a34a" : "#ef4444",
        width: 34,
        height: 34,
        borderRadius: "50%",
        opacity: busy ? 0.7 : 1,
        cursor: busy ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isOn ? (
        <PackageCheck size={16} color="#fff" />
      ) : (
        <Package size={16} color="#fff" />
      )}
    </button>
  );
}

function VariantStatusList({
  item,
  prices,
  variantStatusMap,
  handleVariantStatusToggle,
  setVariantMoreModal,
}) {
  const visiblePrices = (prices || []).slice(0, 3);
  const hiddenCount = Math.max((prices || []).length - 3, 0);

  return (
    <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-hidden">
      {visiblePrices.map((price, idx) => {
        const variantId = String(
          price.id || price.row_id || price.size_id || "",
        );

        const variantState = variantStatusMap[variantId] || {
          stock: price.inStock === "Y" ? "Y" : "N",
          visibility: price.visibility === "Y" ? "Y" : "N",
        };

        const stockEnabled = variantState.stock === "Y";
        const visibilityEnabled = variantState.visibility === "Y";

        return (
          <div
            key={`${variantId}-${idx}`}
            className="flex h-[52px] w-[140px] shrink-0 items-center justify-between rounded-lg border border-gray-200 bg-white px-2 py-1 shadow-sm"
          >
            <div className="min-w-0">
              <div className="max-w-[75px] truncate text-[11px] font-bold text-gray-800">
                {price.variantName || price.size || `Variant ${idx + 1}`}
              </div>

              <div className="mt-1 flex items-center gap-1">
                <span className="text-[9px] font-semibold uppercase text-gray-500">
                  Stock
                </span>

                <button
                  type="button"
                  onClick={() =>
                    handleVariantStatusToggle({
                      variantId,
                      type: "stock",
                    })
                  }
                  className={`relative h-5 w-9 rounded-full transition ${
                    stockEnabled ? "bg-green-500" : "bg-red-400"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold shadow transition ${
                      stockEnabled ? "left-4" : "left-0.5"
                    }`}
                  >
                    {stockEnabled ? "✓" : "✕"}
                  </span>
                </button>
              </div>
            </div>

            <div className="flex flex-col items-center gap-1">
              <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
                #{idx + 1}
              </span>

              <button
                type="button"
                onClick={() =>
                  handleVariantStatusToggle({
                    variantId,
                    type: "visibility",
                  })
                }
                className={`flex h-6 w-6 items-center justify-center rounded-full ${
                  visibilityEnabled
                    ? "bg-green-50 text-green-600"
                    : "bg-gray-100 text-gray-500"
                }`}
                title={visibilityEnabled ? "Visible" : "Hidden"}
              >
                {visibilityEnabled ? (
                  <Eye size={15} strokeWidth={2.4} />
                ) : (
                  <EyeOff size={15} strokeWidth={2.4} />
                )}
              </button>
            </div>
          </div>
        );
      })}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() =>
            setVariantMoreModal({
              itemName: item?.item_name,
              prices: prices || [],
            })
          }
          className="flex h-[52px] w-[58px] shrink-0 items-center justify-center rounded-lg border border-dashed border-green-400 bg-green-50 text-xs font-bold text-green-700 hover:bg-green-100"
          title="View all variants"
        >
          +{hiddenCount}
        </button>
      )}
    </div>
  );
}

function ItemVisibilityButton({ item, itemVisibilityMap, handleHideItem }) {
  const isVisible =
    itemVisibilityMap[item.item_id] ?? item?.prices?.[0]?.visibility === "Y";

  return (
    <div className="ml-1 flex shrink-0 items-center gap-1">
      <div className="text-[11px] font-semibold text-gray-700">Hidden</div>

      <button
        onClick={() => handleHideItem(item)}
        title={isVisible ? "Visible" : "Hidden"}
        className={`flex h-8 w-8 items-center justify-center rounded-full border transition ${
          isVisible
            ? "border-green-200 bg-green-50 text-green-600"
            : "border-red-200 bg-red-50 text-red-500"
        }`}
      >
        {isVisible ? (
          <Eye size={14} strokeWidth={2.3} />
        ) : (
          <EyeOff size={14} strokeWidth={2.3} />
        )}
      </button>
    </div>
  );
}

function VariantMoreModal({
  itemName,
  prices,
  variantStatusMap,
  handleVariantStatusToggle,
  onClose,
}) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white p-4 shadow-xl">
        <div className="mb-4 flex items-center justify-between border-b pb-3">
          <div>
            <h2 className="text-base font-bold text-gray-900">All Variants</h2>
            <p className="text-xs text-gray-500">{itemName}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="grid max-h-[65vh] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2 md:grid-cols-3">
          {(prices || []).map((price, idx) => {
            const variantId = String(
              price.id || price.row_id || price.size_id || "",
            );

            const variantState = variantStatusMap[variantId] || {
              stock: price.inStock === "Y" ? "Y" : "N",
              visibility: price.visibility === "Y" ? "Y" : "N",
            };

            const stockEnabled = variantState.stock === "Y";
            const visibilityEnabled = variantState.visibility === "Y";

            return (
              <div
                key={`${variantId}-${idx}`}
                className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-gray-900">
                      {price.variantName || price.size || `Variant ${idx + 1}`}
                    </div>

                    <div className="mt-0.5 text-xs text-gray-500">
                      ₹{price.price || price.formatted_price || "—"}
                    </div>
                  </div>

                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-500">
                    #{idx + 1}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase text-gray-500">
                      Stock
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        handleVariantStatusToggle({
                          variantId,
                          type: "stock",
                        })
                      }
                      className={`relative h-6 w-12 rounded-full transition ${
                        stockEnabled ? "bg-green-500" : "bg-red-400"
                      }`}
                    >
                      <span
                        className={`absolute top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-bold shadow transition ${
                          stockEnabled ? "left-7" : "left-1"
                        }`}
                      >
                        {stockEnabled ? "✓" : "✕"}
                      </span>
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase text-gray-500">
                      Visibility
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        handleVariantStatusToggle({
                          variantId,
                          type: "visibility",
                        })
                      }
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${
                        visibilityEnabled
                          ? "bg-green-50 text-green-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                      title={visibilityEnabled ? "Visible" : "Hidden"}
                    >
                      {visibilityEnabled ? (
                        <Eye size={17} strokeWidth={2.4} />
                      ) : (
                        <EyeOff size={17} strokeWidth={2.4} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
